import React from "react";
import { Link } from "react-router-dom";
import PermissionsGate from "../../../../../utils/permissionGate";
import { parsePrerequisiteIds } from "../../../../../utils/VersionModulesUtils";
import Badge from "../../../../library/Badge";
import { ExpandableBulletList, ModuleStatusLabel, TableLink } from "../../../../library/DataTable";
import IconButton, { StyledActionGroup, StyledIconLink } from "../../../../library/IconButton";
import { StyledPortalNameStack, StyledPrereqLink } from "./VersionModulesTable.styles";

const getModuleUrl = (moduleId) => `/program-management-center/modules/module/open/${moduleId}`;

/**
 * "Portal Name" cell: the module's portal name (linked) with its status pill beneath it -
 * Marketing Name is dropped per SPEC3, combined into this one cell instead.
 * @param {object} row Module row.
 */
export function PortalNameCell({ row }) {
    return (
        <StyledPortalNameStack>
            <PermissionsGate
                scopes={["pmcpmedit"]}
                RenderError={() => <span title={row.Portal}>{row.Portal || "-"}</span>}
            >
                <TableLink as={Link} to={getModuleUrl(row.Id)} target="_self" title={row.Portal}>
                    {row.Portal || "-"}
                </TableLink>
            </PermissionsGate>

            {row.module_status && <ModuleStatusLabel value={row.module_status} />}
        </StyledPortalNameStack>
    );
}

/**
 * "Study Path" cell: the assigned phase name, or a red "No Study Path" warning badge - the
 * same look as the Assign Modules modal's Study Path column.
 * @param {object} row Module row, carrying `studyPath` (phase name or null).
 */
export function StudyPathCell({ row }) {
    if (!row.studyPath) {
        return (
            <Badge tone="destructive" icon="exclamation-triangle">No Study Path</Badge>
        );
    }

    return (
        <Badge tone="tideBlue" icon="link">{row.studyPath}</Badge>
    );
}

/**
 * "Pre-Requisites" cell: resolves the module's prerequisite ids (a JSON array) against every
 * module in the system, each linking to that prerequisite module.
 * @param {object} row Module row.
 * @param {Map<number, string>} moduleNameById System-wide module id -> Portal name lookup.
 */
export function PreRequisitesCell({ row, moduleNameById }) {
    const prerequisiteLinks = parsePrerequisiteIds(row)
        .filter((prerequisiteId) => moduleNameById.has(prerequisiteId))
        .map((prerequisiteId) => (
            <StyledPrereqLink key={prerequisiteId} to={getModuleUrl(prerequisiteId)}>
                {moduleNameById.get(prerequisiteId)}
            </StyledPrereqLink>
        ));

    return (
        <ExpandableBulletList items={prerequisiteLinks} />
    );
}

/**
 * "Action" cell: the folder-open link plus the unlink button, both running through the same
 * confirm -> progress -> cancel-remaining flow as the bulk bar (a one-item batch), so there's a
 * single unlink implementation.
 * @param {object} row Module row.
 * @param {object} hook State/handlers from `useVersionModules`.
 */
export function ActionsCell({ row, hook }) {
    return (
        <StyledActionGroup>
            <PermissionsGate
                scopes={["pmcpmedit"]}
                RenderError={() => <IconButton icon="folder-open" variant="solid" disabled ariaLabel="Open module" />}
            >
                <StyledIconLink to={getModuleUrl(row.Id)} title="Open module">
                    <IconButton icon="folder-open" variant="solid" ariaLabel="Open module" />
                </StyledIconLink>
            </PermissionsGate>

            <PermissionsGate scopes={["pmdelete"]} errorProps={{ disabled: true, title: "Insufficient Permissions" }}>
                <IconButton
                    icon="unlink"
                    variant="destructive"
                    ariaLabel="Unlink module"
                    disabled={hook.isProcessing || hook.isLocked}
                    disabledReason={hook.isLocked ? "This version's settings are locked" : undefined}
                    onClick={() => hook.startConfirmation([row.Id])}
                />
            </PermissionsGate>
        </StyledActionGroup>
    );
}
